import {
  Card,
  InputNumber,
  Typography,
  Space,
  Row,
  Col,
  Button,
  Slider,
  Tooltip,
  Divider,
} from "antd";
import { toastSuccess, toastError } from "../../utils/toast";
import {
  SettingOutlined,
  BellOutlined,
  MailOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { EmailListSettingCard } from "./EmailListComponent";
import axiosInstance from "../../services/axiosInstance";
import {
  GET_SETTINGS,
  UPDATE_NOTIFICATION_SETTINGS,
  UPDATE_SETTINGS,
} from "../../services/apiEndPoints";
import { INotificationSettings } from "../types/member.types";

const { Text, Title } = Typography;

const getUrgencyConfig = (days: number) => {
  if (days <= 14)
    return { color: "#ef4444", bg: "#fef2f2", border: "#fecaca", label: "Tight window" };
  if (days <= 30)
    return { color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "Standard" };
  if (days <= 60)
    return { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", label: "Comfortable" };
  return { color: "#10b981", bg: "#f0fdf4", border: "#a7f3d0", label: "Very early" };
};

const sliderMarks: Record<number, string> = {
  1: "1",
  30: "30",
  60: "60",
  90: "90",
  120: "120",
  180: "180",
};

interface ReminderCardProps {
  title: string;
  value: number;
  savedValue: number;
  onChange: (value: number) => void;
  onSave: () => void;
  saving: boolean;
  justSaved: boolean;
}

const ReminderCard: React.FC<ReminderCardProps> = ({
  title,
  value,
  savedValue,
  onChange,
  onSave,
  saving,
  justSaved,
}) => {
  const isDirty = value !== savedValue;
  const urgency = getUrgencyConfig(value);

  return (
    <Card
      style={{
        borderRadius: 12,
        height: "100%",
        borderTop: `3px solid ${urgency.color}`,
        transition: "box-shadow 0.2s, border-color 0.2s",
      }}
      styles={{ body: { padding: "20px 24px" } }}
      hoverable
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: urgency.bg,
                border: `1px solid ${urgency.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: urgency.color,
                fontSize: 16,
                flexShrink: 0,
                transition: "all 0.3s",
              }}
            >
              <BellOutlined />
            </div>
            <div>
              <Title level={5} style={{ margin: 0, fontSize: 14 }}>
                {title}
              </Title>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {urgency.label}
              </Text>
            </div>
          </div>
          <Tooltip title="Days before document expiry to send a reminder notification">
            <InfoCircleOutlined style={{ color: "#94a3b8", fontSize: 14, cursor: "help" }} />
          </Tooltip>
        </div>

        <div
          style={{
            textAlign: "center",
            padding: "8px 0 4px",
          }}
        >
          <span
            style={{
              fontSize: 36,
              fontWeight: 700,
              color: urgency.color,
              lineHeight: 1,
              transition: "color 0.3s",
            }}
          >
            {value}
          </span>
          <Text
            type="secondary"
            style={{ display: "block", fontSize: 12, marginTop: 2 }}
          >
            days before expiry
          </Text>
        </div>

        <Slider
          value={value}
          min={1}
          max={180}
          marks={sliderMarks}
          onChange={onChange}
          tooltip={{ formatter: (val) => `${val} days` }}
          styles={{
            track: { background: urgency.color },
            rail: { background: "#e2e8f0" },
          }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            value={value}
            min={1}
            max={180}
            style={{ flex: 1 }}
            onChange={(val) => onChange(val ?? 1)}
            addonAfter="days"
            size="middle"
          />
          <Button
            type="primary"
            onClick={onSave}
            disabled={!isDirty || saving}
            icon={
              saving ? (
                <LoadingOutlined />
              ) : justSaved ? (
                <CheckCircleOutlined />
              ) : undefined
            }
            style={{
              minWidth: 80,
              borderRadius: 8,
              background: justSaved ? "#10b981" : undefined,
              borderColor: justSaved ? "#10b981" : undefined,
            }}
          >
            {saving ? "Saving" : justSaved ? "Saved" : "Save"}
          </Button>
        </div>

        {isDirty && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              background: "#fffbeb",
              borderRadius: 6,
              border: "1px solid #fde68a",
              fontSize: 12,
              color: "#92400e",
            }}
          >
            <InfoCircleOutlined style={{ fontSize: 12 }} />
            Changed from {savedValue} to {value} days — click Save to apply
          </div>
        )}
      </Space>
    </Card>
  );
};

const SettingsPage: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [savedEmails, setSavedEmails] = useState<string[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<
    INotificationSettings[]
  >([]);
  const [savedNotificationSettings, setSavedNotificationSettings] = useState<
    INotificationSettings[]
  >([]);
  const [savingType, setSavingType] = useState<string | null>(null);
  const [justSavedType, setJustSavedType] = useState<string | null>(null);
  const justSavedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const saveNotificationSettings = async (documentType: string) => {
    const setting = notificationSettings.find(
      (ns) => ns.documentType === documentType
    );
    if (!setting) return;

    try {
      setSavingType(documentType);
      await axiosInstance.put(UPDATE_NOTIFICATION_SETTINGS, {
        documentType,
        notificationDays: setting.notificationDays,
      });
      setSavedNotificationSettings((prev) =>
        prev.map((ns) =>
          ns.documentType === documentType
            ? { ...ns, notificationDays: setting.notificationDays }
            : ns
        )
      );
      setJustSavedType(documentType);
      clearTimeout(justSavedTimer.current);
      justSavedTimer.current = setTimeout(() => setJustSavedType(null), 2000);
      toastSuccess(`Reminder for ${documentType} updated`);
    } catch {
      toastError("Failed to save notification settings");
    } finally {
      setSavingType(null);
    }
  };

  const saveEmails = async () => {
    setSavedEmails(emails);
    await axiosInstance.put(UPDATE_SETTINGS, { emailIds: emails });
    toastSuccess("Email recipients saved");
  };

  const getSettingsData = async () => {
    const data = await axiosInstance.get(GET_SETTINGS);
    if (data?.data) {
      const settings = data.data;
      setEmails(settings.emailIds || []);
      setSavedEmails(settings.emailIds || []);
      const ns = settings.notificationSettings || [];
      setNotificationSettings(ns);
      setSavedNotificationSettings(ns.map((s: INotificationSettings) => ({ ...s })));
    }
  };

  useEffect(() => {
    getSettingsData();
  }, []);

  const getSavedDays = (documentType: string) =>
    savedNotificationSettings.find((ns) => ns.documentType === documentType)
      ?.notificationDays ?? 0;

  const totalTypes = notificationSettings.length;
  const configuredCount = notificationSettings.filter(
    (ns) => ns.notificationDays > 0
  ).length;

  return (
    <div>
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            Settings
          </Title>
          <Text className="page-subtitle">
            Configure notifications, recipients, and reminder preferences
          </Text>
        </Col>
      </Row>

      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <MailOutlined style={{ color: "#1677ff", fontSize: 16 }} />
          <Text strong style={{ fontSize: 15 }}>
            Email Recipients
          </Text>
        </div>
        <EmailListSettingCard
          title="Notification Recipients"
          description="Add email addresses that should receive reminder notifications."
          value={emails}
          onChange={setEmails}
          onSave={saveEmails}
          isDirty={JSON.stringify(emails) !== JSON.stringify(savedEmails)}
        />
      </div>

      {notificationSettings.length > 0 && (
        <div>
          <Divider style={{ margin: "8px 0 20px" }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#1677ff",
                  fontSize: 15,
                }}
              >
                <SettingOutlined />
              </div>
              <div>
                <Text strong style={{ fontSize: 15, display: "block", lineHeight: 1.3 }}>
                  Reminder Configuration
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Set how many days before expiry each document type triggers a reminder
                </Text>
              </div>
            </div>
            <Tooltip title={`${configuredCount} of ${totalTypes} document types configured`}>
              <div
                style={{
                  padding: "4px 12px",
                  borderRadius: 20,
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#16a34a",
                }}
              >
                {configuredCount}/{totalTypes} configured
              </div>
            </Tooltip>
          </div>
          <Row gutter={[16, 16]}>
            {notificationSettings.map((docType: INotificationSettings) => (
              <Col xs={24} md={8} key={docType.documentType}>
                <ReminderCard
                  title={docType.documentType}
                  value={docType.notificationDays}
                  savedValue={getSavedDays(docType.documentType)}
                  onChange={(days) => {
                    setNotificationSettings((prev) =>
                      prev.map((ns) =>
                        ns.documentType === docType.documentType
                          ? { ...ns, notificationDays: days }
                          : ns
                      )
                    );
                  }}
                  onSave={() =>
                    saveNotificationSettings(docType.documentType)
                  }
                  saving={savingType === docType.documentType}
                  justSaved={justSavedType === docType.documentType}
                />
              </Col>
            ))}
          </Row>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
